import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import TeamTemplateSchema, { TeamTemplateJSON, TeamTemplateParams } from "work/team-templates/_schema/team-template-schema";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

export default class TeamTemplate {
  public static get EXAMPLE(): TeamTemplate {
    return new TeamTemplate({
      id: Guid.generate(),
      created: new AHDate(),
      isMasked: false,
      name: new VendorTeamName("Example Team Template"),
      description: "This is an example team template.",
      leader: EntityMember.EXAMPLE,
      members: [EntityMember.EXAMPLE],
      marketplaceId: Guid.generate(),
      avatarId: Guid.generate(),
    });
  }

  public static fromJSON(json: TeamTemplateJSON): TeamTemplate {
    const parsed = TeamTemplateSchema.deserialize(json);
    return new TeamTemplate(parsed);
  }

  public static fromJSONArray(jsonArray: TeamTemplateJSON[]): TeamTemplate[] {
    return jsonArray.map((j) => TeamTemplate.fromJSON(j));
  }

  public readonly id?: Guid;
  public readonly created?: AHDate;
  public readonly isMasked: boolean = false;
  public readonly name: VendorTeamName;
  public readonly description?: string;
  public readonly leader: EntityMember;
  public readonly members: EntityMember[] = [];
  public readonly marketplaceId?: Guid;
  public readonly avatarId?: Guid;

  public constructor(params: TeamTemplateParams) {
    this.id = params.id ?? undefined;
    this.created = params.created ?? undefined;
    this.isMasked = params.isMasked ?? false;
    this.name = params.name;
    this.description = params.description ?? undefined;
    this.leader = params.leader;
    this.members = params.members;
    this.marketplaceId = params.marketplaceId ?? undefined;
    this.avatarId = params.avatarId ?? undefined;
  }

  public toJSON(): TeamTemplateJSON {
    return TeamTemplateSchema.serialize(this);
  }

  public isEqualTo(other: TeamTemplate | null | undefined): boolean {
    if (!other) return false;
    const idEqual = this.id?.isEqualTo(other.id) ?? false;
    const createdEqual = this.created?.isEqualTo(other.created) ?? false;
    const nameEqual = Boolean((!this.name && !other.name) || (this.name && other.name && this.name.isEqualTo(other.name)));
    const marketplaceIdEquals = this.marketplaceId?.isEqualTo(other.marketplaceId) ?? false;
    const avatarIdEquals = this.avatarId?.isEqualTo(other.avatarId) ?? false;

    return (
      idEqual   
      && createdEqual
      && this.isMasked === other.isMasked
      && nameEqual
      && this.description === other.description
      && this.leader.isEqualTo(other.leader)
      && this.members.length === other.members.length
      && this.members.every((member, index) => member.isEqualTo(other.members[index]))
      && marketplaceIdEquals
      && avatarIdEquals
    );
  }

  public toString(): string {
    return `TeamTemplate { 
      id: ${this.id?.value ?? "undefined"}, 
      created: ${this.created?.toString() ?? "undefined"},
      isMasked: ${this.isMasked},
      name: ${this.name?.value ?? "undefined"}, 
      description: ${this.description ?? "undefined"},
      leader: ${this.leader.toString()},
      members: [${this.members.map((m) => m.toString()).join(", ")}],
      marketplaceId: ${this.marketplaceId?.value ?? "undefined"},
      avatarId: ${this.avatarId?.value ?? "undefined"}
    }`;
  }

  public userIsReferenced(userId?: Guid): boolean {
    if (!userId) return false;

    return (
      this.leader.userId.isEqualTo(userId) || this.members.some((member) => member.userId.isEqualTo(userId))
    );
  }
}
